"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { NewsCard } from "@/components/shared";
import { getApiUrl, fetchApi } from "@/lib/api-utils";
import { getFormattedDate, getImageUrl } from "@/utils/utils";

interface HomePageNewsClientProps {
  lang: string;
  initialNewsItems: any[];
  newsCategories: any[];
  homepageContent: any;
  staticCategories: string[];
}

export default function HomePageNewsClient({
  lang,
  initialNewsItems,
  newsCategories,
  homepageContent,
  staticCategories,
}: HomePageNewsClientProps) {
  const [activeTab, setActiveTab] = useState("All News");
  const [newsItems, setNewsItems] = useState<any[]>(initialNewsItems);
  const [newsLoading, setNewsLoading] = useState(false);

  // Localized text service
  const localizedText = {
    en: {
      loading: "Loading...",
      noNewsFound: "No news found in this category.",
      noDataAvailable: "No data available.",
      error: "Failed to load content. Please try again.",
      retry: "Retry",
    },
    jp: {
      loading: "読み込み中...",
      noNewsFound: "このカテゴリにはニュースが見つかりません。",
      noDataAvailable: "利用可能なデータがありません。",
      error: "コンテンツの読み込みに失敗しました。もう一度お試しください。",
      retry: "再試行",
    },
  };

  const text =
    localizedText[lang as keyof typeof localizedText] || localizedText["en"];

  // Animation variants - exact match from original
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Fetch news by category
  const fetchNewsByCategory = async (category: string) => {
    try {
      setNewsLoading(true);
      setActiveTab(category);

      // Build API URL with proper parameters
      const params: Record<string, string | number> = { limit: 3 };

      // For 'All News' category, don't add category filter to get all news
      // For specific categories, add category filter
      if (category !== "All News" && category) {
        newsCategories.forEach((cat) => {
          if (cat.title === category) {
            params.category = cat.documentId;
          }
        });
        if (!params?.category) {
          setNewsItems([]);
          return;
        }
      }

      const response = await fetchApi<any>(
        getApiUrl("/homepage", lang, params)
      );

      setNewsItems(response.news.news || []);
    } catch (error) {
      console.error("Error fetching news by category:", error);
      setNewsItems([]);
    } finally {
      setNewsLoading(false);
    }
  };

  return (
    <motion.section
      className="py-10 sm:py-16 lg:py-20 w-full overflow-visible relative z-10"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 lg:min-h-[582px]">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6 md:gap-6 mb-8 sm:mb-12">
          {/* News Title - Left */}
          <div className="flex-shrink-0">
            <h2 className="text-[#0a1119] text-3xl sm:text-4xl lg:text-[50px] font-medium leading-tight lg:leading-[58px] tracking-[-1.44px]">
              {homepageContent?.news?.title || "News"}
            </h2>
          </div>

          {/* Navigation tabs - Center */}
          <div className="flex flex-wrap h-10 md:flex-nowrap gap-3 sm:gap-4 lg:gap-6 xl:gap-8 lg:justify-center flex-1 md:overflow-x-auto md:scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {staticCategories.map((category: string, index: number) => (
              <button
                key={index}
                onClick={() => fetchNewsByCategory(category)}
                className="relative cursor-pointer"
              >
                <span
                  className={`news-menu-link text-sm sm:text-[16px] font-medium leading-[16px] tracking-wide md:whitespace-nowrap ${
                    activeTab === category
                      ? " text-[#1a5852] active"
                      : " text-[#0a1119] hover:text-[#1a5852] transition-colors"
                  }`}
                >
                  {category}
                </span>
              </button>
            ))}
          </div>

          {/* View News List Button - Right */}
          <div className="flex-shrink-0">
            <Link
              href={`/${lang}/news`}
              className="flex items-center gap-[10px] border border-[#1a5852] text-[#1a5852] hover:bg-[#1a5852] hover:text-white px-[24px] py-[17px] rounded-full transition-all duration-200 cursor-pointer"
            >
              <ChevronRight
                size={14}
                className="transition-colors duration-200"
              />
              <span className="text-sm sm:text-[16px] font-medium leading-[16px] capitalize">
                {homepageContent?.news?.ctaText || "View News List"}
              </span>
            </Link>
          </div>
        </div>

        {/* News Cards - Responsive Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 "
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {newsLoading ? (
            // Loading state
            [1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 rounded-lg h-[300px] mb-4"></div>
                <div className="h-7 bg-gray-200 rounded mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))
          ) : newsItems.length > 0 ? (
            newsItems.map((newsItem, index) => (
              <motion.div
                key={`${activeTab}-${newsItem.id}-${index}`}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <NewsCard
                  image={
                    getImageUrl(newsItem.images?.[0]?.url) ||
                    "/images/news/news-1.png"
                  }
                  title={newsItem.title}
                  category={newsItem.category}
                  date={getFormattedDate(newsItem?.publishedDate)}
                  excerpt={newsItem.excerpt}
                  alt={newsItem.title}
                  href={`/${lang}/news/${newsItem.slug}`}
                />
              </motion.div>
            ))
          ) : (
            <div className="col-span-1 md:col-span-2 lg:col-span-3 flex items-center justify-center pt-[150px] pb-[114px] lg:pt-[200px] md:pb-16">
              <p className="text-gray-500 text-lg">
                No news found in this category
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </motion.section>
  );
}
